import React from 'react';
import { X } from 'lucide-react';

// Stil dili Glowy OnSite (WidgetStudio) ile aynı: küçük punto, gri-200 kenar, indigo-200 odak halkası.
export const inputCls = 'w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 disabled:bg-gray-50 disabled:text-gray-500';
export const btnPrimary = 'inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed [&_svg]:w-3.5 [&_svg]:h-3.5';
export const btnGhost = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-800 disabled:opacity-40 disabled:cursor-not-allowed [&_svg]:w-3.5 [&_svg]:h-3.5';

// Ayar panelindeki bölüm (stüdyodaki Group): kenarlıksız, alt çizgiyle ayrılır.
export function Card({ title, hint, actions, children }) {
  return (
    <section className="px-4 py-3.5 border-b border-gray-100">
      {(title || actions) && (
        <div className="flex items-start justify-between gap-3 mb-2.5">
          <div className="min-w-0">
            {title && <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{title}</h3>}
            {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

// Sonuç sekmelerinde gri zemin üstündeki beyaz kutu.
export function Panel({ title, hint, actions, children, className = '' }) {
  return (
    <div className={`rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}>
      {(title || actions) && (
        <div className="flex items-start justify-between gap-3 px-4 pt-3.5 pb-2">
          <div className="min-w-0">
            {title && <h3 className="text-[13px] font-semibold text-gray-900">{title}</h3>}
            {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className="px-4 pb-4">{children}</div>
    </div>
  );
}

export function Field({ label, hint, children }) {
  return (
    <label className="block mb-2.5 last:mb-0">
      <span className="block text-[11px] text-gray-500 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-gray-400 mt-1">{hint}</span>}
    </label>
  );
}

export function Toggle({ checked, onChange, label }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-xs text-gray-600">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 shrink-0 rounded-full transition-colors ${checked ? 'bg-indigo-600' : 'bg-gray-300'}`}
      >
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`} />
      </button>
    </div>
  );
}

export function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          disabled={o.disabled}
          title={o.title}
          onClick={() => !o.disabled && onChange(o.value)}
          className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md ${value === o.value ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'} ${o.disabled ? 'opacity-40 cursor-not-allowed hover:text-gray-500' : ''}`}
        >
          {o.dot && <span className={`w-1.5 h-1.5 rounded-full ${o.dot}`} />}
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Her satırı ayrı input olan liste (min/max sınırlı, istersen sıralama oklu)
export function StringListEditor({ items, onChange, max, min = 0, placeholder, multiline = false, sortable = false, addLabel = 'Ekle' }) {
  const set = (i, v) => onChange(items.map((x, j) => (j === i ? v : x)));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div>
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-1.5 mb-1.5">
          {multiline
            ? <textarea rows={2} className={inputCls} value={it} placeholder={placeholder} onChange={(e) => set(i, e.target.value)} />
            : <input className={inputCls} value={it} placeholder={placeholder} onChange={(e) => set(i, e.target.value)} />}
          {sortable && (
            <div className="flex flex-col">
              <button type="button" className="text-gray-400 hover:text-gray-700 text-xs leading-none px-1" onClick={() => move(i, -1)} disabled={i === 0}>▲</button>
              <button type="button" className="text-gray-400 hover:text-gray-700 text-xs leading-none px-1 mt-1" onClick={() => move(i, 1)} disabled={i === items.length - 1}>▼</button>
            </div>
          )}
          <button type="button" disabled={items.length <= min} onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-2 text-gray-300 hover:text-red-600 disabled:opacity-30" title="Sil">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      {items.length < max && (
        <button type="button" className={btnGhost} onClick={() => onChange([...items, ''])}>+ {addLabel}</button>
      )}
      <span className="ml-2 text-[11px] text-gray-400">{items.length}/{max}{min ? ` (en az ${min})` : ''}</span>
    </div>
  );
}

export function Modal({ title, onClose, children, footer, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4" onClick={onClose}>
      <div className={`bg-white rounded-lg shadow-xl w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto">{children}</div>
        {footer && <div className="px-4 py-3 border-t border-gray-100 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export function Drawer({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="text-[15px] font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

export function Badge({ tone = 'gray', children }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-600', green: 'bg-green-50 text-green-700',
    amber: 'bg-amber-50 text-amber-700', red: 'bg-red-50 text-red-700', indigo: 'bg-indigo-50 text-indigo-700',
  };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${tones[tone]}`}>{children}</span>;
}
